import {
  AppBar,
  Avatar,
  Breadcrumbs,
  Chip,
  IconButton,
  Link as BreadcrumbLink,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@material-ui/core";
import { Brightness4, Brightness7, Home } from "@material-ui/icons";
import styles from "./TopBar.module.css";
import { generatePath, Link } from "react-router-dom";
import React, { Suspense } from "react";
import { Skeleton } from "@material-ui/lab";
import APIErrorBoundary from "./APIErrorBoundary";
import DiggerLogo from "./DiggerLogo";
import { logout } from "./Authentication";
import { useRouteMatch } from "../util";
import { useResource } from "../useResource";
import API from "../api";
import { DiggerProductVersion } from "../globals";
import analytics from "../analytics";
import { useIntercom } from "react-use-intercom";

export default function TopBar(props: {
  darkMode: boolean;
  onDarkmodeChange: (darkMode: boolean) => void;
}) {
  return (
    <AppBar position="sticky" variant="elevation" elevation={1} color="inherit">
      <Toolbar>
        <Link to="/">
          <DiggerLogo className={styles.logo} />
        </Link>
        <Chip size="small" label={`v${DiggerProductVersion}`} disabled />
        <TopbarBreadcrumbs />
        <IconButton
          title="Toggle light/dark theme"
          onClick={() => props.onDarkmodeChange(!props.darkMode)}
        >
          {props.darkMode ? <Brightness7 /> : <Brightness4 />}
        </IconButton>
        <APIErrorBoundary context="Loading user profile">
          <Suspense
            fallback={
              <IconButton edge="end">
                <Skeleton variant="circle" width={40} height={40} />
              </IconButton>
            }
          >
            <UserAvatar />
          </Suspense>
        </APIErrorBoundary>
      </Toolbar>
    </AppBar>
  );
}

const genericPath = "/projects/:projectName/:subresourceType?/:subresourceID?";

function TopbarBreadcrumbs() {
  const match = useRouteMatch(genericPath);
  let breadcrumbs = [];
  if (match) {
    const { projectName, subresourceType, subresourceID } = match.params;

    breadcrumbs.push(
      <ProjectBreadCrumbItem
        projectName={projectName}
        last={subresourceID === undefined}
        key={projectName}
      />
    );

    if (subresourceType && subresourceID) {
      switch (match.params.subresourceType) {
        case "services":
          breadcrumbs.push(
            <Suspense fallback={"..."} key={subresourceID}>
              <ServiceBreadCrumbItem
                projectName={projectName}
                serviceID={subresourceID}
              />
            </Suspense>
          );
          break;
        case "environments":
          breadcrumbs.push(
            <Suspense fallback={"..."} key={subresourceID}>
              <EnvironmentBreadCrumbItem
                projectName={projectName}
                environmentID={subresourceID}
              />
            </Suspense>
          );
      }
    }
  }

  return (
    <div className={styles.breadcrumbs}>
      <div>
        <Breadcrumbs color="inherit">
          {match ? (
            <BreadcrumbLink color="inherit" component={Link} to="/">
              <Home />
            </BreadcrumbLink>
          ) : (
            <div>Choose a project</div>
          )}
          {breadcrumbs}
        </Breadcrumbs>
      </div>
    </div>
  );
}

function ProjectBreadCrumbItem(props: { projectName: string; last: boolean }) {
  return (
    <TopbarBreadcrumbItem
      text={props.projectName}
      url={generatePath(genericPath, {
        projectName: props.projectName,
      })}
      last={props.last}
    />
  );
}

function ServiceBreadCrumbItem(props: {
  projectName: string;
  serviceID: string;
}) {
  const [service] = useResource(
    API.services.read,
    props.serviceID,
    props.projectName
  );
  return (
    <TopbarBreadcrumbItem
      text={service.name}
      url={generatePath(genericPath, {
        projectName: props.projectName,
        subresourceType: "services",
        subresourceID: props.serviceID,
      })}
      last={true}
    />
  );
}

function EnvironmentBreadCrumbItem(props: {
  projectName: string;
  environmentID: string;
}) {
  const [environment] = useResource(
    API.environments.read,
    props.environmentID,
    props.projectName
  );
  return (
    <TopbarBreadcrumbItem
      text={environment.name}
      url={generatePath(genericPath, {
        projectName: props.projectName,
        subresourceType: "services",
        subresourceID: props.environmentID,
      })}
      last={true}
    />
  );
}

function TopbarBreadcrumbItem(props: {
  text?: string;
  url: string;
  last: boolean;
}) {
  if (!props.last) {
    return (
      <BreadcrumbLink color="inherit" component={Link} to={props.url}>
        {props.text}
      </BreadcrumbLink>
    );
  } else {
    return <Typography color="inherit">{props.text}</Typography>;
  }
}

function UserAvatar() {
  const [profile] = useResource(API.profile.read);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    handleClose();
    logout();
  };

  analytics.identify(profile.pk, {
    name: profile.username,
    email: profile.email,
  });

  // update intercom
  const { update } = useIntercom();
  update({
    name: profile.username,
    email: profile.email || "anon@anon.com",
  });

  return (
    <>
      <IconButton
        onClick={handleClick}
        title={profile.first_name + " " + profile.last_name}
        edge="end"
      >
        <Avatar src={profile.avatar_url}>
          {profile.first_name[0] ?? ""}
          {profile.last_name[0] ?? ""}
        </Avatar>
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        elevation={1}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem disabled>Signed in as {profile.username}</MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
    </>
  );
}
